import { Box, Link, StatusLabel, Text } from '@factorialco/deprecated-design-system'
import { Currency } from 'dinero.js'
import { ContactFragment, InvoiceFragment } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import i18n from 'modules/core/lib/i18n'
import MoneyWithCurrency from 'modules/core/lib/money'

import DropItem from 'modules/expenses/components/common/DropItem'
import { invoiceStatusTagType, mapStatusText } from 'modules/finance/lib/invoices'

type Props = {
  invoice: InvoiceFragment & { vendor?: ContactFragment | null }
}

const InvoiceDetailContent = ({ invoice }: Props) => {
  const { navigation } = useNavigation()

  const { status, vendor, createdAt, totalAmountCents, file, id, currency } = invoice

  const {
    access: { locale },
  } = useCurrentSession()

  const amount = MoneyWithCurrency({
    amount: Number(totalAmountCents),
    // eslint-disable-next-line no-type-assertion/no-type-assertion
    currency: currency as Currency,
    locale,
  })

  return (
    <Box width="full" justifyContent="flexStart" alignItems="flexStart" gap="s32">
      <Box width="full" justifyContent="flexStart" alignItems="flexStart" gap="s16">
        <Text.Header2 color="grey1000">
          {i18n.t(
            'it_management.software_setups.software_payment_detail.invoice.titles.cost_information'
          )}
        </Text.Header2>
      </Box>
      <Box width="full" flexDirection="row" justifyContent="flexStart" alignItems="flexStart">
        <Box width="full" textAlign="left" gap="s8">
          <Text weight="medium" size="300" color="grey1000">
            {i18n.t('it_management.software_setups.software_payment_detail.invoice.type')}
          </Text>
          <Box height="s24">
            <StatusLabel type="info">
              {i18n.t('it_management.software_setups.software_payment_detail.invoice.cost_type')}
            </StatusLabel>
          </Box>
        </Box>
        <Box width="full" textAlign="left" gap="s8">
          <Text weight="medium" size="300" color="grey1000">
            {i18n.t('it_management.software_setups.software_payment_detail.invoice.status')}
          </Text>
          <Box height="s24">
            <StatusLabel type={invoiceStatusTagType(status)}>{mapStatusText(status)}</StatusLabel>
          </Box>
        </Box>
      </Box>
      <Box width="full" flexDirection="row" justifyContent="flexStart" alignItems="flexStart">
        <Box width="full" textAlign="left" gap="s8">
          <Text weight="medium" size="300" color="grey1000">
            {i18n.t('it_management.software_setups.software_payment_detail.invoice.fiscal_name')}
          </Text>
          <Box height="s24">
            <Text weight="normal" size="300" color="grey800">
              {vendor?.name}
            </Text>
          </Box>
        </Box>
        <Box width="full" textAlign="left" gap="s8">
          <Text weight="medium" size="300" color="grey1000">
            {i18n.t(
              'it_management.software_setups.software_payment_detail.invoice.transaction_date'
            )}
          </Text>
          <Box height="s24">
            <Text weight="normal" size="300" color="grey800">
              {createdAt}
            </Text>
          </Box>
        </Box>
      </Box>
      <Box width="full" flexDirection="row" justifyContent="flexStart" alignItems="flexStart">
        <Box width="full" textAlign="left" gap="s8">
          <Text weight="medium" size="300" color="grey1000">
            {i18n.t('it_management.software_setups.software_payment_detail.invoice.amount')}
          </Text>
          <Box height="s24">
            <Text weight="normal" size="300" color="grey800">
              {amount}
            </Text>
          </Box>
        </Box>
      </Box>
      <Box width="full" justifyContent="flexStart" gap="s16">
        <Text.Header2 color="grey1000">
          {i18n.t('it_management.software_setups.software_payment_detail.invoice.titles.document')}
        </Text.Header2>
        {file && (
          <DropItem
            key={file.id || file.filename}
            file={{
              id: file.id.toString(),
              name: file.filename,
              size: file.size.toString(),
              type: file.contentType,
              preview: file.url,
            }}
            downloadable
            showInViewer={false}
          />
        )}
      </Box>
      <Box width="full" justifyContent="flexStart" alignItems="flexStart" gap="s16">
        <Text.Header2 color="grey1000">
          {i18n.t(
            'it_management.software_setups.software_payment_detail.invoice.titles.invoice_information'
          )}
        </Text.Header2>
        <Link.Href
          href={navigation.finance.invoices.list.invoiceId.fullPath({
            invoiceId: id,
          })}
          target="_blank"
          external={false}
        >
          {i18n.t('it_management.software_setups.software_payment_detail.invoice.invoice_info_cta')}
        </Link.Href>
      </Box>
    </Box>
  )
}

export default InvoiceDetailContent
